import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  FormControl,
  OutlinedInput,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import ReusableModal from '../ReusableModal/ReusableModal';
import { listScenarios, runScenario } from '../../api/scenarios';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../store/snackBarSlice';

// Import the Scenario interface from the API file
import { Scenario } from '../../api/scenarios';

interface RunScenarioModalProps {
  open: boolean;
  onClose: () => void;
  workspaceID: string;
  workspaceName: string;
}

const RunScenarioModal: React.FC<RunScenarioModalProps> = ({
  open,
  onClose,
  workspaceID,
  workspaceName,
}) => {
  const dispatch = useDispatch();
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [selectedScenarios, setSelectedScenarios] = useState<number[]>([]);
  const [runResponse, setRunResponse] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const loadScenarios = async () => {
    try {
      setLoading(true);
      // Get both generic and contract-specific scenarios in one call
      const response = await listScenarios(1, 100, workspaceID ? [parseInt(workspaceID)] : null, "INCLUDE_GENERIC");
      
      // Sort scenarios by name for better usability
      const sortedScenarios = [...response.items].sort((a, b) => 
        a.scenario_name.localeCompare(b.scenario_name)
      );
      // Add a small delay to ensure UI updates properly
      setTimeout(() => {
        setScenarios(sortedScenarios);
        setSelectedScenarios([]); // Reset selection when loading new scenarios
      }, 100);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `Failed to load scenarios: ${error}`,
          severity: "error",
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadScenarios();
    }
  }, [open]);

  const handleRunScenario = async () => {
    if (selectedScenarios.length === 0) {
      dispatch(
        showSnackbar({
          message: "Please select at least one scenario",
          severity: "error",
        }),
      );
      return;
    }

    try {
      // Run each selected scenario
      for (const scenarioId of selectedScenarios) {
        await runScenario(scenarioId, [workspaceID]);
      }
      
      setRunResponse("success");
      dispatch(
        showSnackbar({
          message: `${selectedScenarios.length > 1 ? 'Your requests have' : 'Your request has'} been queued, check back later once complete`,
          severity: "success",
        }),
      );
      setTimeout(() => onClose(), 2000);
    } catch (error) {
      dispatch(
        showSnackbar({
          message: `Failed to run scenario: ${error}`,
          severity: "error",
        }),
      );
    }
  };

  const handleScenarioChange = (event: any) => {
    const value = event.target.value;
    setSelectedScenarios(typeof value === 'string' ? [] : value);
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Run Scenario"
      content={
        <Box>
          <Box sx={{mt:2}}>
            <FormControl
              sx={{ width: "800px" }}
              variant="outlined"
            >
              <Typography>Workspace</Typography>
              <OutlinedInput
                value={workspaceName}
                disabled
                fullWidth
              />
            </FormControl>
          </Box>

          <Box sx={{mt:2, mb:2, p:2, backgroundColor: "#f5f7ff", borderRadius: 1, border: "1px solid #e0e4f6"}}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{mb:1}}>About Running Scenarios</Typography>
            <Typography variant="body2">
              This process applies a selected analysis scenario to the contract workspace. The analysis will identify potential value leakage opportunities based on the scenario parameters.
            </Typography>
            <Typography variant="body2" sx={{mt:1}}>
              You will be notified once the scenario run is complete. Results will be available in the Opportunities section.
            </Typography>
          </Box>

          <Box sx={{mt:2}}>
            <FormControl
              sx={{ width: "800px" }}
              variant="outlined"
            >
              <Typography>Scenario</Typography>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Select
                  multiple
                  value={selectedScenarios}
                  onChange={handleScenarioChange}
                  fullWidth
                  displayEmpty
                  renderValue={(selected) => {
                    if ((selected as number[]).length === 0) {
                      return <Typography variant="body2">Select scenario(s)</Typography>;
                    }
                    
                    return (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as number[]).map((scenarioId) => {
                          const scenario = scenarios.find(s => s.scenario_id === scenarioId);
                          return (
                            <Box key={scenarioId} sx={{ bgcolor: '#e0e0e0', borderRadius: '4px', px: 1, py: 0.5, fontSize: '0.75rem' }}>
                              {scenario?.scenario_name}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  }}
                >
                  <MenuItem disabled value="">
                    <Typography variant="body2">Select a scenario</Typography>
                  </MenuItem>
                  
                  {/* Group scenarios into Generic and Contract-specific */}
                  <MenuItem disabled value="generic_header">
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Generic Scenarios</Typography>
                  </MenuItem>
                  {scenarios
                    .filter(scenario => scenario.contract_id === null)
                    .map((scenario) => (
                      <MenuItem key={scenario.scenario_id} value={scenario.scenario_id}>
                        {scenario.scenario_name}
                      </MenuItem>
                    ))
                  }
                  
                  {scenarios.some(scenario => scenario.contract_id !== null) && (
                    <MenuItem disabled value="contract_header">
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
                        Contract-Specific Scenarios for {workspaceName}
                      </Typography>
                    </MenuItem>
                  )}
                  {scenarios
                    .filter(scenario => scenario.contract_id !== null)
                    .map((scenario) => (
                      <MenuItem key={scenario.scenario_id} value={scenario.scenario_id}>
                        {scenario.scenario_name} {scenario.contract_workspace ? `(${scenario.contract_workspace})` : ''}
                      </MenuItem>
                    ))
                  }
                  
                  {scenarios.length === 0 && (
                    <MenuItem disabled value="no_scenarios">
                      <Typography variant="body2" color="text.secondary">
                        No scenarios available. Please generate scenarios first.
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              )}
            </FormControl>
          </Box>

          {runResponse === "success" && (
            <Box sx={{mt:2, display: "flex", alignItems: "center"}}>
              <Box sx={{ color: "success.main", mr: 1, display: "flex" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </Box>
              <Typography color="success.main">
                Your request has been queued, check back later once complete
              </Typography>
            </Box>
          )}
        </Box>
      }
      actions={
        <>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          {runResponse !== "success" && (
            <Button 
              variant="contained" 
              onClick={handleRunScenario}
              disabled={selectedScenarios.length === 0 || loading}
            >
              Run Scenario
            </Button>
          )}
        </>
      }
    />
  );
};

export default RunScenarioModal;
